import { retimeDraft, isDraftProtected } from './scheduleDraft';
import { cleanParallel } from './scheduleTimeline';
import { newId } from './scheduleTimes';

export const scriptLocation = scene => String(scene?.location || 'Unknown location').trim().toUpperCase().replace(/\s+/g,' ');
export const sceneActivities = (day,id) => day.entries.filter(e=>e.kind==='activity'&&e.parentOccurrenceId===id);
export const sceneRows = day => day.entries.filter(e=>!e.parentOccurrenceId);
const protectedChange = (before,after) => {
  for(const d of before) for(const e of d.entries) if(isDraftProtected(e)) {
    const n=after.find(x=>x.dayNumber===d.dayNumber)?.entries.find(x=>x.id===e.id);
    if(!n||JSON.stringify(n)!==JSON.stringify(e))return `"${e.name||e.id}" has a locked or fixed time. Change that setting before moving its group.`;
  }
  return null;
};
export const retimeSceneDays = (days,next) => {
  const out=next.map(d=>({...d,entries:retimeDraft(cleanParallel(d.entries),days.find(x=>x.dayNumber===d.dayNumber)?.entries||d.entries)}));
  const error=protectedChange(days,out);return error?{error}:{days:out};
};
export const moveSceneBundle = (days,from,ids,to,index) => {
  const source=days.find(d=>d.dayNumber===from),target=days.find(d=>d.dayNumber===to);
  if(!source||!target)return {error:'The shooting day is unavailable.'};
  const chosen=new Set(ids),entries=source.entries.filter(e=>chosen.has(e.id)||chosen.has(e.parentOccurrenceId));
  if(!entries.length)return {error:'Choose a scene or group to move.'};
  const locked=entries.find(isDraftProtected);if(locked)return {error:`"${locked.name||locked.id}" has a locked or fixed time and cannot move.`};
  const selectedIds=new Set(entries.map(e=>e.id));
  if(entries.some(e=>e.parallelGroupId&&source.entries.some(x=>x.parallelGroupId===e.parallelGroupId&&!selectedIds.has(x.id))))return {error:'Move all activities in the parallel group together.'};
  const next=days.map(d=>({...d,entries:d.entries.filter(e=>!selectedIds.has(e.id))}));
  const dest=next.find(d=>d.dayNumber===to);if(!dest)return {error:'The destination is unavailable.'};
  let at=Math.max(0,Math.min(index,dest.entries.length));
  // Inserting before a scene also precedes its prep; do not split another scene bundle.
  const before=dest.entries[at];
  const parent=before?.parentOccurrenceId||before?.id;
  if(parent && entries.some(e=>e.kind==='scene')) {
    const first=dest.entries.findIndex(e=>e.id===parent||e.parentOccurrenceId===parent);
    if(first>=0)at=first;
  }
  dest.entries.splice(at,0,...entries);
  return retimeSceneDays(days,next);
};
export const addSceneActivities = (days,dayNumber,parent,types,templates) => {
  const next=days.map(d=>{
    if(d.dayNumber!==dayNumber)return d;
    const at=d.entries.findIndex(e=>e.id===parent&&e.kind==='scene');if(at<0)return d;
    const existing=new Set(d.entries.filter(e=>e.parentOccurrenceId===parent).map(e=>e.activityType));
    const activities=[...new Set(types)].filter(type=>!existing.has(type)).map(type=>({id:newId(),kind:'activity',parentOccurrenceId:parent,activityType:type,
      name:templates.find(t=>t.type===type)?.name||'Custom Activity',durationMin:30,start:null,manualTime:false,locked:false,notes:''}));
    const entries=[...d.entries];entries.splice(at,0,...activities);return {...d,entries};
  });
  return retimeSceneDays(days,next);
};
export const moveSelectedActivities = (days, ids, parentId) => {
  const target=days.find(d=>d.entries.some(e=>e.id===parentId&&e.kind==='scene'));
  if(!target)return {error:'Choose a destination scene occurrence.'};
  const selected=new Set(ids),activities=days.flatMap(d=>d.entries.filter(e=>selected.has(e.id)));
  if(!activities.length||activities.some(e=>e.kind!=='activity'||!e.parentOccurrenceId))return {error:'Choose scene activities to move.'};
  if(activities.some(isDraftProtected))return {error:'Unlock or clear fixed activity times before moving them.'};
  if(activities.some(e=>days.some(d=>d.entries.some(parent=>parent.id===e.parentOccurrenceId&&parent.locked))))return {error:'Unlock the parent scene before reassigning its activities.'};
  if(activities.some(e=>e.parallelGroupId&&days.some(d=>d.entries.some(x=>x.parallelGroupId===e.parallelGroupId&&!selected.has(x.id)))))return {error:'Select every activity in the parallel group.'};
  const existing=new Set(target.entries.filter(e=>e.parentOccurrenceId===parentId&&!selected.has(e.id)).map(e=>e.activityType));
  if(activities.some(e=>existing.has(e.activityType)))return {error:'The destination scene already has this activity type.'};
  const next=days.map(d=>({...d,entries:d.entries.filter(e=>!selected.has(e.id))}));
  const day=next.find(d=>d.dayNumber===target.dayNumber);if(!day)return {error:'The destination day is unavailable.'};
  const at=day.entries.findIndex(e=>e.id===parentId);
  day.entries.splice(at,0,...activities.map(e=>({...e,parentOccurrenceId:parentId})));
  return retimeSceneDays(days,next);
};
export const copySceneActivities = (days,fromId,toIds) => {
  const source=days.flatMap(d=>d.entries).filter(e=>e.parentOccurrenceId===fromId);
  const next=days.map(d=>{
    const entries=[];
    for(const e of d.entries){
      if(e.kind==='scene'&&toIds.includes(e.id)&&e.id!==fromId){
        const existing=new Set(d.entries.filter(x=>x.parentOccurrenceId===e.id).map(x=>x.activityType));
        const groups=new Map();for(const a of source.filter(x=>!existing.has(x.activityType))){if(a.parallelGroupId&&!groups.has(a.parallelGroupId))groups.set(a.parallelGroupId,newId());
          entries.push({...a,id:newId(),parentOccurrenceId:e.id,start:null,manualTime:false,locked:false,...(a.parallelGroupId?{parallelGroupId:groups.get(a.parallelGroupId)}:{})});}
      }
      entries.push(e);
    }
    return {...d,entries};
  });return retimeSceneDays(days,next);
};